import { useMeClient } from "@common/queries";
import { useClientIntake } from "@modules/client/queries";
import { useMemo } from "react";
import { ClientIntakeData } from "./types";
import {
  deserializeClientSSN,
  deserializeClientDate,
  deserializeClientPhoneNumber,
} from "./utils.frontend";

const useIntakeFormValues = () => {
  const { data: client, isLoading: isLoadingClient } = useMeClient();
  const { data: intakeFormData, isLoading: isLoadingIntake } =
    useClientIntake();

  const coClientExists = !!intakeFormData?.coClient?.EMAIL;
  const isLoading = useMemo(
    () => isLoadingClient || isLoadingIntake,
    [isLoadingClient, isLoadingIntake]
  );

  const values = useMemo(() => {
    if (isLoading) return;

    const result: { client: ClientIntakeData; coClient?: ClientIntakeData } = {
      client: {
        FIRST_NAME:
          intakeFormData?.client?.FIRST_NAME ?? client?.firstName ?? "",
        LAST_NAME: intakeFormData?.client?.LAST_NAME ?? client?.lastName ?? "",
        EMAIL: intakeFormData?.client?.EMAIL ?? client?.email ?? "",
        PHONE_NUMBER: deserializeClientPhoneNumber(
          intakeFormData?.client?.PHONE_NUMBER ??
            (client?.auth0MfaPhoneNumber?.replace(" ", "") || "+15555555555")
        ),
        SSN: deserializeClientSSN(intakeFormData?.client?.SSN ?? ""),
        DOB: deserializeClientDate(intakeFormData?.client?.DOB ?? ""),
        DRIVER_LICENSE_NUMBER:
          intakeFormData?.client?.DRIVER_LICENSE_NUMBER ?? "",
        DRIVER_LICENSE_STATE:
          intakeFormData?.client?.DRIVER_LICENSE_STATE ?? "",
        DRIVER_LICENSE_EXP: deserializeClientDate(
          intakeFormData?.client?.DRIVER_LICENSE_EXP ?? ""
        ),
        ADDRESS_STREET:
          intakeFormData?.client?.ADDRESS_STREET ?? client?.addressStreet ?? "",
        ADDRESS_STREET_2:
          intakeFormData?.client?.ADDRESS_STREET_2 ??
          client?.addressStreetLineTwo ??
          "",
        ADDRESS_CITY:
          intakeFormData?.client?.ADDRESS_CITY ?? client?.addressCity ?? "",
        ADDRESS_STATE:
          intakeFormData?.client?.ADDRESS_STATE ?? client?.addressState ?? "",
        ADDRESS_ZIP:
          intakeFormData?.client?.ADDRESS_ZIP ?? client?.addressZip ?? "",
        MAILING_ADDRESS_STREET:
          intakeFormData?.client?.MAILING_ADDRESS_STREET ?? "",
        MAILING_ADDRESS_STREET_2:
          intakeFormData?.client?.MAILING_ADDRESS_STREET_2 ?? "",
        MAILING_ADDRESS_CITY:
          intakeFormData?.client?.MAILING_ADDRESS_CITY ?? "",
        MAILING_ADDRESS_STATE:
          intakeFormData?.client?.MAILING_ADDRESS_STATE ?? "",
        MAILING_ADDRESS_ZIP: intakeFormData?.client?.MAILING_ADDRESS_ZIP ?? "",
        HAS_FINRA_ASSOCIATION: intakeFormData?.client?.HAS_FINRA_ASSOCIATION,
        EMPLOYMENT_STATUS: intakeFormData?.client?.EMPLOYMENT_STATUS,
        EMPLOYER_NAME: intakeFormData?.client?.EMPLOYER_NAME,
        OCCUPATION: intakeFormData?.client?.OCCUPATION,
      },
    };
    if (coClientExists) {
      result.coClient = {
        FIRST_NAME: intakeFormData?.coClient?.FIRST_NAME ?? "",
        LAST_NAME: intakeFormData?.coClient?.LAST_NAME ?? "",
        EMAIL: intakeFormData?.coClient?.EMAIL ?? "",
        PHONE_NUMBER: deserializeClientPhoneNumber(
          intakeFormData?.coClient?.PHONE_NUMBER ?? ""
        ),
        SSN: deserializeClientSSN(intakeFormData?.coClient?.SSN ?? ""),
        DOB: deserializeClientDate(intakeFormData?.coClient?.DOB ?? ""),
        DRIVER_LICENSE_NUMBER:
          intakeFormData?.coClient?.DRIVER_LICENSE_NUMBER ?? "",
        DRIVER_LICENSE_STATE:
          intakeFormData?.coClient?.DRIVER_LICENSE_STATE ?? "",
        DRIVER_LICENSE_EXP: deserializeClientDate(
          intakeFormData?.coClient?.DRIVER_LICENSE_EXP ?? ""
        ),
        ADDRESS_STREET:
          intakeFormData?.client?.ADDRESS_STREET ?? client?.addressStreet ?? "",
        ADDRESS_STREET_2:
          intakeFormData?.client?.ADDRESS_STREET_2 ??
          client?.addressStreetLineTwo ??
          "",
        ADDRESS_CITY:
          intakeFormData?.client?.ADDRESS_CITY ?? client?.addressCity ?? "",
        ADDRESS_STATE:
          intakeFormData?.client?.ADDRESS_STATE ?? client?.addressState ?? "",
        ADDRESS_ZIP:
          intakeFormData?.client?.ADDRESS_ZIP ?? client?.addressZip ?? "",
        MAILING_ADDRESS_STREET:
          intakeFormData?.client?.MAILING_ADDRESS_STREET ?? "",
        MAILING_ADDRESS_STREET_2:
          intakeFormData?.client?.MAILING_ADDRESS_STREET_2 ?? "",
        MAILING_ADDRESS_CITY:
          intakeFormData?.client?.MAILING_ADDRESS_CITY ?? "",
        MAILING_ADDRESS_STATE:
          intakeFormData?.client?.MAILING_ADDRESS_STATE ?? "",
        MAILING_ADDRESS_ZIP: intakeFormData?.client?.MAILING_ADDRESS_ZIP ?? "",
        EMPLOYMENT_STATUS: intakeFormData?.coClient?.EMPLOYMENT_STATUS,
        EMPLOYER_NAME: intakeFormData?.coClient?.EMPLOYER_NAME,
        OCCUPATION: intakeFormData?.coClient?.OCCUPATION,
        HAS_FINRA_ASSOCIATION:
          intakeFormData?.coClient?.HAS_FINRA_ASSOCIATION ?? false,
      };
    }
    return result;
  }, [
    client?.addressCity,
    client?.addressState,
    client?.addressStreet,
    client?.addressStreetLineTwo,
    client?.addressZip,
    client?.auth0MfaPhoneNumber,
    client?.email,
    client?.firstName,
    client?.lastName,
    coClientExists,
    intakeFormData?.client?.ADDRESS_CITY,
    intakeFormData?.client?.ADDRESS_STATE,
    intakeFormData?.client?.ADDRESS_STREET,
    intakeFormData?.client?.ADDRESS_STREET_2,
    intakeFormData?.client?.ADDRESS_ZIP,
    intakeFormData?.client?.DOB,
    intakeFormData?.client?.DRIVER_LICENSE_EXP,
    intakeFormData?.client?.DRIVER_LICENSE_NUMBER,
    intakeFormData?.client?.DRIVER_LICENSE_STATE,
    intakeFormData?.client?.EMAIL,
    intakeFormData?.client?.EMPLOYER_NAME,
    intakeFormData?.client?.EMPLOYMENT_STATUS,
    intakeFormData?.client?.FIRST_NAME,
    intakeFormData?.client?.HAS_FINRA_ASSOCIATION,
    intakeFormData?.client?.LAST_NAME,
    intakeFormData?.client?.MAILING_ADDRESS_CITY,
    intakeFormData?.client?.MAILING_ADDRESS_STATE,
    intakeFormData?.client?.MAILING_ADDRESS_STREET,
    intakeFormData?.client?.MAILING_ADDRESS_STREET_2,
    intakeFormData?.client?.MAILING_ADDRESS_ZIP,
    intakeFormData?.client?.OCCUPATION,
    intakeFormData?.client?.PHONE_NUMBER,
    intakeFormData?.client?.SSN,
    intakeFormData?.coClient?.DOB,
    intakeFormData?.coClient?.DRIVER_LICENSE_EXP,
    intakeFormData?.coClient?.DRIVER_LICENSE_NUMBER,
    intakeFormData?.coClient?.DRIVER_LICENSE_STATE,
    intakeFormData?.coClient?.EMAIL,
    intakeFormData?.coClient?.EMPLOYER_NAME,
    intakeFormData?.coClient?.EMPLOYMENT_STATUS,
    intakeFormData?.coClient?.FIRST_NAME,
    intakeFormData?.coClient?.HAS_FINRA_ASSOCIATION,
    intakeFormData?.coClient?.LAST_NAME,
    intakeFormData?.coClient?.OCCUPATION,
    intakeFormData?.coClient?.PHONE_NUMBER,
    intakeFormData?.coClient?.SSN,
    isLoading,
  ]);
  return { isLoading, values, coClientExists };
};

export { useIntakeFormValues };
